import React from 'react';
import { Link } from 'react-router-dom';

import {
  finleyAssert,
  finleyAssertUnreachable,
} from '@finley-technologies/common';

import { useUploadContext } from '~/packages/dataPipeline/hooks/UploadContext';
import { ActionableAlert } from '~/packages/dataPipeline/ui/components/ActionableAlert';
import { PipelineDetailsPageLink } from '~/packages/dataPipeline/ui/uploads/PipelineDetailsPageLink';

export const UploadDrawerAlert = () => {
  const {
    canUpload,
    completeRecord,
    meshRunId,
    instances,
  } = useUploadContext();

  if (!canUpload) {
    return (
      <ActionableAlert status="info" mb="10px">
        Pipeline changed. You can not upload files to this pipeline.
      </ActionableAlert>
    );
  }

  if (!meshRunId) {
    return null;
  }

  // since mesRunId is available we can assume that completeRecord is available
  finleyAssert(completeRecord, 'completeRecord must be available');
  const status = completeRecord.meshRunRecord.status;
  const pipelineLinkElement = <PipelineDetailsPageLink meshRunId={meshRunId} />;
  switch (status) {
    case 'inProgress':
      return (
        <ActionableAlert
          status="info"
          mb="10px"
          actionElement={pipelineLinkElement}
        >
          Pipeline in progress
        </ActionableAlert>
      );

    case 'error': {
      const masterLoanTapeArtifact = completeRecord.meshDocument.artifacts.find(
        (artifact) => artifact.type === 'masterLoanTape',
      );
      if (masterLoanTapeArtifact) {
        const erroredMasterLoanTapeInstance = instances.find(
          (instance) =>
            instance.artifactId === masterLoanTapeArtifact.id &&
            instance.status === 'error',
        );
        if (erroredMasterLoanTapeInstance) {
          return (
            <ActionableAlert
              status="error"
              mb="10px"
              actionElement={pipelineLinkElement}
            >
              There are discrepancies in the pipeline—the loan tape does not
              align with Finley’s receivable records.
            </ActionableAlert>
          );
        }
      }
      return (
        <ActionableAlert
          status="error"
          mb="10px"
          actionElement={pipelineLinkElement}
        >
          There are errors in the pipeline.
        </ActionableAlert>
      );
    }

    case 'success':
      return null;

    case 'waitingForInput':
      return (
        <ActionableAlert
          status="warning"
          mb="10px"
          actionElement={pipelineLinkElement}
        >
          Upload all required files
        </ActionableAlert>
      );

    case 'waitingForAssignment': {
      const assignmentLinkElement = (
        <Link
          to="/assignments"
          style={{ color: 'blue', textDecoration: 'underline' }}
        >
          Confirm Assignment
        </Link>
      );

      return (
        <ActionableAlert status="warning" actionElement={assignmentLinkElement}>
          Review your assignments.
        </ActionableAlert>
      );
    }

    case 'waitingForVerificationApproval':
      return (
        <ActionableAlert status="warning">Waiting for approval</ActionableAlert>
      );

    case 'expired':
      return (
        <ActionableAlert status="warning">
          This pipeline is expired.
        </ActionableAlert>
      );
    default:
      return finleyAssertUnreachable(status);
  }
};
